import React from 'react';
import { useDarkMode } from '../contexts/DarkModeContext';

const ProductViewSkeleton = () => {
  const { isDarkMode } = useDarkMode();
  
  return (
  <div className={`flex-1 rounded-xl shadow-lg p-8 border-t-[6px] border-red-500 rounded-t-2xl animate-pulse transition-all duration-500 ${
    isDarkMode 
      ? 'bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
      : 'bg-white'
  }`}>
    <div className="flex flex-col md:flex-row gap-8">
      {/* Image & Gallery Skeleton - Left Side */}
      <div className="flex-1 w-full">
        {/* Main Image Skeleton */}
        <div className={`w-full aspect-[9/16] rounded-3xl mb-4 transition-colors duration-300 ${
          isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
        }`} />
        
        {/* Dots Indicator Skeleton */}
        <div className="flex justify-center gap-2 mt-4 mb-6">
          {[...Array(4)].map((_, idx) => (
            <span key={idx} className={`w-3 h-3 rounded-full transition-colors duration-300 ${
              isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
            }`} />
          ))}
        </div>
        
        {/* Attachments Section Skeleton */}
        <div className="mt-6">
          <div className={`h-6 rounded w-24 mb-3 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
          }`} />
          <div className="grid grid-cols-3 gap-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className={`w-[140px] h-[90px] rounded-2xl transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              }`} />
            ))}
          </div>
        </div>
      </div>
      
      {/* Product Info Skeleton - Right Side */}
      <div className="flex-[2] min-w-[300px] w-full">
        {/* Title and Check Profile Link */}
        <div className="flex flex-wrap justify-between items-center gap-2 mb-2">
          <div className={`h-8 rounded w-3/4 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
          }`} />
          <div className={`h-6 rounded w-24 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-600' : 'bg-gray-100'
          }`} />
        </div>
        
        {/* Description Skeleton */}
        <div className="mb-4" style={{ minHeight: '60px' }}>
          <div className={`h-4 rounded w-full mb-2 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
          }`} />
          <div className={`h-4 rounded w-5/6 mb-2 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
          }`} />
          <div className={`h-4 rounded w-4/6 mb-2 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-600' : 'bg-gray-100'
          }`} />
          <div className={`h-4 rounded w-3/6 transition-colors duration-300 ${
            isDarkMode ? 'bg-gray-600' : 'bg-gray-100'
          }`} />
        </div>
        
        {/* Info Grid Skeleton */}
        <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 rounded-xl p-3 sm:p-4 mb-5 sm:mb-6 transition-all duration-300 ${
          isDarkMode ? 'bg-gray-800/50 border border-gray-700/50' : 'bg-gray-50'
        }`}>
          {[...Array(8)].map((_, i) => (
            <div key={i} className="flex flex-col items-center">
              <div className={`h-5 rounded w-8 mb-1 transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              }`} />
              <div className={`h-3 rounded w-12 transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-600' : 'bg-gray-100'
              }`} />
            </div>
          ))}
        </div>
        
        {/* Price and Buy Now Section */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 sm:gap-8 mb-2 mt-6 sm:mt-8">
          {/* Left: Price and Buy Button */}
          <div className="flex flex-col items-start gap-2 flex-1 min-w-[160px] w-full">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className={`h-8 rounded w-24 transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              }`} />
              <div className={`h-6 rounded w-16 transition-colors duration-300 ${
                isDarkMode ? 'bg-gray-600' : 'bg-gray-100'
              }`} />
            </div>
            <div className={`mt-2 w-full sm:w-48 h-12 sm:h-14 rounded-xl transition-colors duration-300 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
            }`} />
          </div>
          
          {/* Right: Delivery Info and Payment Icons */}
          <div className="flex flex-col md:items-end gap-2 flex-1 min-w-[160px] w-full mt-4 sm:mt-0">
            <div className="flex flex-row gap-3 sm:gap-6 mb-2 md:justify-end">
              <div className="flex items-center gap-1 sm:gap-2">
                <div className={`w-5 h-5 rounded transition-colors duration-300 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
                <div className={`h-4 rounded w-20 transition-colors duration-300 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              </div>
              <div className="flex items-center gap-1 sm:gap-2">
                <div className={`w-5 h-5 rounded transition-colors duration-300 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
                <div className={`h-4 rounded w-24 transition-colors duration-300 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              </div>
            </div>
            <div className="flex flex-row gap-2 sm:gap-3 mt-2">
              {[...Array(5)].map((_, i) => (
                <div key={i} className={`w-14 h-8 rounded transition-colors duration-300 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                }`} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  );
};

export default ProductViewSkeleton; 